/**
 * Modelo del asistente "Generación de Órdenes de Servicio Masivas".
 * Solo maquetación: tipos + datos de referencia; sin llamadas a API.
 */

export type MassOsPeriodMode = 'month' | 'range';
export type MassOsClientMode = 'b2c' | 'b2b';

export type MassOsPaymentCondition = 'PREPAGO' | 'POSTPAGO';

export interface MassOsProviderOption {
  id: string;
  name: string;
  condicionPago?: MassOsPaymentCondition;
  condicionPagoDias?: number;
}

export interface MassOsLabeledOption {
  id: string;
  label: string;
  icon?: string;
}

export interface MassOsMonthOption {
  value: number;
  label: string;
}

export interface MassOsGenerationForm {
  providerId: string | null;
  periodMode: MassOsPeriodMode;
  year: number | null;
  months: number[];
  rangeStart: Date | null;
  rangeEnd: Date | null;
  clientMode: MassOsClientMode;
  b2cSourceIds: string[];
  b2bClientIds: string[];
  serviceTypeIds: string[];
}

export const MASS_OS_STEPS = [
  'Filtrar y Seleccionar Servicios',
  'Revisar Servicios',
  'Emitir Órdenes de Servicio'
];

export type MassOsGenerationMode = 'os' | 'np';

export const MASS_OS_MODE_STEPS: Record<MassOsGenerationMode, string[]> = {
  os: MASS_OS_STEPS,
  np: [
    'Filtrar y Seleccionar Servicios',
    'Revisar Servicios',
    'Emitir Notas de Pedido'
  ]
};

export const MASS_OS_YEAR_OPTIONS = [2024, 2025, 2026, 2027, 2028, 2029, 2030];

export const MASS_OS_MONTH_OPTIONS: MassOsMonthOption[] = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' }
];

/** Cliente corporativo B2B (maquetación). */
export interface MassOsB2bClient {
  id: string;
  name: string;
}

/** Origen de clientes B2C (maquetación). */
export const MASS_OS_B2C_SOURCE_OPTIONS: MassOsLabeledOption[] = [
  { id: 'web-sales', label: 'Ventas Web' },
  { id: 'marketing', label: 'Marketing' },
  { id: 'referidos', label: 'Explorandes Referidos' },
  { id: 'operaciones', label: 'Explorandes Operaciones' },
  { id: 'call-center', label: 'Call Center' },
  { id: 'agencias-aliadas', label: 'Agencias Aliadas' }
];

/** Clientes B2B fake según docs/mass-os-layout.md §5. */
export const MASS_OS_B2B_CLIENT_MOCKS: MassOsB2bClient[] = [
  { id: '1', name: 'Adventure Trip Inc.' },
  { id: '2', name: 'Adventures Within Reach' },
  { id: '3', name: 'Alpin Travel GmbH' },
  { id: '4', name: 'Ancient Summit' },
  { id: '5', name: 'Avanti Destinations' },
  { id: '6', name: 'Aventyrssresor' },
  { id: '7', name: 'B4Experience' },
  { id: '8', name: 'Backroads' },
  { id: '9', name: 'Betchart Expeditions Inc.' },
  { id: '10', name: 'Vaya Adventures' },
  { id: '11', name: 'Walks Worldwide' },
  { id: '12', name: 'Explorer Chick' },
  { id: '13', name: 'Knowmad Adventures' },
  { id: '14', name: 'Gane & Marshall International Ltd.' }
];

/** Tipos de servicio seleccionables (maquetación). */
export const MASS_OS_SERVICE_TYPE_OPTIONS: MassOsLabeledOption[] = [
  { id: 'alimentos', label: 'Alimentos y Bebidas', icon: 'coffee' },
  { id: 'operador', label: 'Operador', icon: 'briefcase' },
  { id: 'alojamiento', label: 'Alojamiento', icon: 'bed' },
  { id: 'guia', label: 'Guía', icon: 'compass' },
  { id: 'personal-campo', label: 'Personal de Campo', icon: 'backpack' },
  { id: 'transporte', label: 'Transporte', icon: 'bus' },
  { id: 'coordinador', label: 'Coordinador', icon: 'headset' },
  { id: 'ticket', label: 'Ticket', icon: 'ticket' },
  { id: 'otros', label: 'Otros', icon: 'dots' }
];

/** Mapea los ids de tipo de servicio del formulario a los valores de la Core API. */
export const MASS_OS_SERVICE_TYPE_TO_QUERY: Record<string, string> = {
  alimentos: 'ALIMENTOSYBEBIDAS',
  operador: 'OPERADOR',
  alojamiento: 'ALOJAMIENTO',
  guia: 'GUIA',
  'personal-campo': 'PERSONALDECAMPO',
  transporte: 'TRANSPORTE',
  coordinador: 'COORDINADOR',
  ticket: 'TICKET',
  otros: 'OTROS'
};

/**
 * Convierte el código de tipo de servicio devuelto por la Core API
 * (p.ej. 'ALIMENTOSYBEBIDAS', 'ALOJAMIENTO') al label legible de la UI
 * (p.ej. 'Alimentos y Bebidas', 'Alojamiento'). Si no hay match, devuelve el valor.
 */
export function massOsServiceTypeLabel(
  tipo: string | null | undefined
): string {
  if (!tipo) {
    return '';
  }
  const raw = String(tipo).toUpperCase().replace(/\s+/g, '');
  const option = MASS_OS_SERVICE_TYPE_OPTIONS.find(
    (opt) => (MASS_OS_SERVICE_TYPE_TO_QUERY[opt.id] ?? '').toUpperCase() === raw
  );
  return option?.label ?? String(tipo);
}

/* ================= Step 2: Revisar Servicios ================= */

export type MassOsClientType = 'b2c' | 'b2b';

export type MassOsCurrency = 'PEN' | 'USD';

/** Ticket Core renderizable (getTicketeriaTicketsByService), paint-ready. */
export interface MassOsTicket {
  id?: string;
  createdAt?: string;
  paxTickets?: {
    ticketAdulto?: number | null;
    ticketEstudiante?: number | null;
    ticketNacional?: number | null;
    ticketNino?: number | null;
  } | null;
  objTarifa?: {
    id?: string | null;
    monedaTarifa?: string | null;
    tipoCambio?: number | null;
    tarifaTicket?: {
      ticketAdulto?: number | null;
      ticketEstudiante?: number | null;
      ticketNacional?: number | null;
      ticketNino?: number | null;
    } | null;
  } | null;
  total?: number | null;
  moneda?: string | null;
  estadoTicket?: string | null;
  hasDocument?: boolean;
  hasInvalidFechaOperacion?: boolean | null;
  order?: {
    orderId?: string | null;
    orderNumber?: string | null;
    orderStatus?: string | null;
    orderObservation?: string | null;
    scheduledPaymentDate?: string | null;
    paidAt?: string | null;
    createdAt?: string | null;
  } | null;
  /** Selección local para crear OS por ticket. */
  checked?: boolean;
  /** Número de tickets por tipo (derivado de paxTickets). */
  ticketTipos?: Array<{ cantidad: string; tipo: string }>;
  /** Código de reserva del ticket (paso 3, agrupación de OS). */
  codigoReserva?: string | null;
  /** Observaciones del ticket (paso 3, agrupación de OS). */
  observaciones?: string | null;
  /** Fecha de pago final por ticket (paso 3, agrupación de OS). */
  fechaPagoProgramada?: Date | null;
}

/** Servicio de un itinerario (misma forma que la grilla OS Masivas). */
export interface MassOsItineraryService {
  id: string;
  serviceDay: number;
  fecha: Date;
  tipoServicio: string;
  nombreServicio: string;
  /** Proveedor del servicio. */
  nombre: string;
  /** Id del proveedor del servicio (desde providerSnapshot). */
  providerId?: string | null;
  /** Condiciones de pago del proveedor (desde providerSnapshot). */
  providerCondicionesPagos?: {
    condicionPago?: string | null;
    plazoDiasFit?: number | null;
  } | null;
  moneda: MassOsCurrency;
  pax: number;
  /** Pasajeros (pax) del servicio, según paxQuantity de la fuente. */
  paxQuantity?: number;
  /** Cantidad de personal del servicio (cantPersonal). */
  cantPersonal?: number;
  costo: number;
  /** Fecha de pago programada (asignada desde el dialog de sincronización). */
  fechaPagoProgramada?: Date;
  /** Código de reserva (asignada desde el dialog de sincronización). */
  codigoReserva?: string | null;
  /** Observaciones de la OS (asignadas desde el dialog de sincronización). */
  observaciones?: string | null;
  /** Número de la Orden de Servicio (si el servicio ya genera una OS). */
  numOS?: string | null;
  /** Id de la Orden de Servicio en backend (para abrir el PDF). */
  orderId?: string | null;
  /** Id de la Nota de Pedido (si el servicio ya genera una NP). */
  purchaseNoteId?: string | null;
  /** Nota de Pedido vinculada (para el resumen NP Masivas). */
  purchaseNote?: {
    purchaseNoteId?: string | null;
    purchaseNoteNumber?: string | null;
  } | null;
  /** Id del itinerario al que pertenece (inyectado en el preload para tickets). */
  idItinerary?: string | null;
  /** PK de itinerary_services para APIs de tickets (mismo que `id`). */
  serviceId?: string | null;
  /** Expand de tickets del servicio TICKET. */
  tickets?: MassOsTicket[] | null;
  ticketsLoaded?: boolean;
  ticketsLoading?: boolean;
  expanded?: boolean;
  /** Estado agregado del padre TICKET (CREADO | EN_PROCESO). */
  estadoServicioTickets?: string | null;
  hasOrderedTickets?: boolean;
  /**
   * Origen del servicio (devuelto por getProgrammingServicesByIds):
   * - `add`: adición de servicio → seleccionable.
   * - `upgrade`: catálogo SSFF sin producto → seleccionable.
   * - `ssff`: catálogo SSFF con producto → NO seleccionable para OS.
   * - `null`: servicio normal.
   */
  displayTypeService: MassOsServiceOrigin;
  /** Total del servicio (para validación `file.total > 0`). */
  total?: number | null;
}

/**
 * Origen del servicio frente al catálogo SSFF (para stickers en la grilla):
 * - `ssff`: catálogo SSFF con producto → NO válido para OS (bloqueado).
 * - `upgrade`: ssffproduct sin productId → válido (upgrade del catálogo).
 * - `add`: serviceAdd → válido (adición de servicio).
 */
export type MassOsServiceOrigin = 'ssff' | 'add' | 'upgrade' | null;

/** Normaliza el `displayTypeService` recibido por getProgrammingServicesByIds. */
export function toMassOsServiceOrigin(value: unknown): MassOsServiceOrigin {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'add' || normalized === 'upgrade' || normalized === 'ssff') {
    return normalized as MassOsServiceOrigin;
  }
  return null;
}

/** Labels legibles de los stickers de origen. */
export const MASS_OS_SERVICE_ORIGIN_LABEL: Record<
  Exclude<MassOsServiceOrigin, null>,
  string
> = {
  ssff: 'SSFF',
  add: 'ADD',
  upgrade: 'UPGRADE'
};

/** Hint a partir de un origen ya resuelto (displayTypeService). */
export function massOsServiceOriginHintOf(origin: MassOsServiceOrigin): string {
  switch (origin) {
    case 'ssff':
      return 'Servicio de catálogo SSFF — no genera OS';
    case 'add':
      return 'Adición de servicio (service add) — genera OS';
    case 'upgrade':
      return 'Upgrade de catálogo SSFF — genera OS';
    default:
      return '';
  }
}

/** Itinerario (file) con sus servicios; nivel padre de la tabla expandible. */
export interface MassOsItinerary {
  id: string;
  fileNumber: string;
  agencia: string;
  /** Persona encargada (columna DS). */
  usuario: string;
  clientType: MassOsClientType;
  servicios: MassOsItineraryService[];
  /** Ids de servicios del itinerario (devueltos por getProgrammingServicesPage). */
  serviceIds: string[];
  /** Fecha de pago programada a nivel de itinerario (cuando target = per-itinerary). */
  fechaPagoProgramada?: Date;
}

const MOCK_TRIP_START = new Date(2026, 2, 9);

function buildMockServices(
  prefix: string,
  rows: Array<[number, string, string, string, MassOsCurrency, number, number]>
): MassOsItineraryService[] {
  return rows.map(
    ([day, tipo, actividad, proveedor, moneda, pax, costo], index) => ({
      id: `${prefix}-s${index + 1}`,
      serviceDay: day,
      fecha: new Date(
        MOCK_TRIP_START.getFullYear(),
        MOCK_TRIP_START.getMonth(),
        MOCK_TRIP_START.getDate() + day - 1
      ),
      tipoServicio: tipo,
      nombreServicio: actividad,
      nombre: proveedor,
      moneda,
      pax,
      costo,
      displayTypeService: null
    })
  );
}

/** Etiqueta del periodo elegido en el paso 1 para el resumen del paso 2. */
export function buildMassOsPeriodLabel(form: MassOsGenerationForm): string {
  if (!form) {
    return '';
  }

  if (form.periodMode === 'month') {
    const monthLabels = form.months
      .slice()
      .sort((a, b) => a - b)
      .map(
        (value) =>
          MASS_OS_MONTH_OPTIONS.find((m) => m.value === value)?.label ?? ''
      )
      .filter(Boolean);
    const period = monthLabels.length
      ? monthLabels.join(' - ')
      : 'Periodo por mes';
    return `Files ${period} ${form.year ?? ''}`.trim();
  }

  const format = (date: Date | null): string =>
    date
      ? `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`
      : '';

  const start = format(form.rangeStart);
  const end = format(form.rangeEnd);

  if (!start && !end) {
    return 'Files';
  }

  return `Files ${start || '—'} – ${end || '—'}`;
}

export function buildInitialMassOsGenerationForm(): MassOsGenerationForm {
  return {
    providerId: null,
    periodMode: 'range',
    year: null,
    months: [],
    rangeStart: null,
    rangeEnd: null,
    clientMode: 'b2c',
    b2cSourceIds: [],
    b2bClientIds: [],
    serviceTypeIds: []
  };
}

/* ================= Dialog: Sincronizar Fecha de Pago ================= */

export type MassOsPaymentDateStatus =
  | 'automatico'
  | 'modificado'
  | 'cambio-masivo';

/** Estado de un ticket dentro del wizard de sincronización (servicios TICKET). */
export interface MassOsTicketPaymentState {
  ticketId: string;
  /** Nombre del servicio padre (para mostrar en el resumen). */
  nombreServicio: string;
  serviceDay: number;
  fecha: Date;
  total: number;
  moneda: MassOsCurrency;
  /** Fecha calculada automáticamente = hoy + condicionPagoDias (independiente de la fecha del servicio). */
  fechaAutomatica: Date;
  /** Fecha editada manualmente por el usuario (null = usa la automática). */
  fechaManual: Date | null;
  /** Estado del cambio. */
  status: MassOsPaymentDateStatus;
  /** Código de reserva (paso 2). */
  codigoReserva: string | null;
  /** Indica si el código fue ingresado manualmente (no debe ser reemplazado por el masivo). */
  codigoManual: boolean;
  /** Observaciones de la OS a la que pertenece el ticket (paso 3). */
  observaciones?: string | null;
}

/** Estado de un servicio dentro del wizard de sincronización. */
export interface MassOsServicePaymentState {
  serviceId: string;
  serviceDay: number;
  fecha: Date;
  tipoServicio: string;
  nombreServicio: string;
  nombre: string;
  moneda: MassOsCurrency;
  pax: number;
  costo: number;
  /** Fecha calculada automáticamente = hoy + condicionPagoDias (independiente de la fecha del servicio). */
  fechaAutomatica: Date;
  /** Fecha editada manualmente por el usuario (null = usa la automática). */
  fechaManual: Date | null;
  /** Estado del cambio. */
  status: MassOsPaymentDateStatus;
  /** Código de reserva (paso 2). */
  codigoReserva: string | null;
  /** Indica si el código fue ingresado manualmente (no debe ser reemplazado por el masivo). */
  codigoManual: boolean;
  /** Observaciones de la OS a la que pertenece el servicio (paso 3). */
  observaciones?: string | null;
  /** true si el servicio es TICKET y desglosa en tickets en el wizard. */
  isTicket?: boolean;
  /** Tickets seleccionados del servicio (solo TICKET; cada ticket = una OS). */
  tickets?: MassOsTicketPaymentState[] | null;
}

/** Estado de un itinerario dentro del wizard de sincronización. */
export interface MassOsItineraryPaymentState {
  itineraryId: string;
  fileNumber: string;
  agencia: string;
  usuario: string;
  /** Fecha masiva aplicada a nivel de itinerario (null = sin masivo). */
  fechaMasiva: Date | null;
  /** Código masivo aplicado a nivel de itinerario (null = sin masivo). */
  codigoReservaMasivo: string | null;
  /** Estados individuales de cada servicio. */
  serviceStates: MassOsServicePaymentState[];
}

export interface DialogSyncPaymentDateData {
  itineraries: MassOsItinerary[];
  selectedServiceIds: string[];
  provider: MassOsProviderOption | null;
  /** Tickets seleccionados por servicio (serviceId → ticketIds) para servicios TICKET. */
  selectedTickets?: Record<string, string[]> | null;
  /** Modo del wizard: 'os' (default, Orden de Servicio) o 'np' (Nota de Pedido). */
  mode?: MassOsGenerationMode;
}

export interface DialogSyncPaymentDateResult {
  confirmed: boolean;
  itineraryStates: MassOsItineraryPaymentState[];
  /** OS creadas por el backend (si se generó y el modo es OS). */
  createdOrders?: Array<{
    orderId: string;
    orderNumber: string;
    serviceIds: string[];
    commissionPercent?: number | null;
    commissionAmount?: number | null;
    baseAmount?: number | null;
    total?: number | null;
  }> | null;
  /** NP creadas por el backend (si se generó y el modo es NP). */
  createdPurchaseNotes?: Array<{
    purchaseNoteId: string;
    purchaseNoteNumber: string;
    serviceIds: string[];
    commissionPercent?: number | null;
    commissionAmount?: number | null;
    baseAmount?: number | null;
    total?: number | null;
  }> | null;
  /** Servicios rechazados (si se generó). */
  rejectedServices?: Array<{
    serviceId: string;
    reason: string;
    message: string;
  }> | null;
  /** Advertencias no bloqueantes (si se generó). */
  warnings?: Array<{ reason: string; message: string }> | null;
  /** Cantidad de OS/NP creadas. */
  createdCount?: number;
}
